import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Link, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import LegalPage from './LegalPage';

function extractDocumentMetadata(markdown, fallbackTitle) {
  const normalizedMarkdown = markdown.replace(/\r\n/g, '\n').trim();
  const titleMatch = normalizedMarkdown.match(/^#\s+(.+)$/m);
  const updatedAtMatch = normalizedMarkdown.match(/^\*\*Last Updated:\*\*\s*(.+)$/m);

  let content = normalizedMarkdown;

  if (titleMatch) {
    content = content.replace(titleMatch[0], '').trim();
  }

  if (updatedAtMatch) {
    content = content.replace(updatedAtMatch[0], '').trim();
  }

  return {
    title: titleMatch?.[1]?.trim() || fallbackTitle,
    updatedAt: updatedAtMatch?.[1]?.trim() || '',
    content,
  };
}

function slugifyHeading(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function flattenChildrenToText(children) {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(flattenChildrenToText).join('');
  if (children && typeof children === 'object' && children.props?.children) {
    return flattenChildrenToText(children.props.children);
  }
  return '';
}

function extractHeadings(markdown) {
  return markdown
    .split('\n')
    .map((line) => line.match(/^(##|###)\s+(.+)$/))
    .filter(Boolean)
    .map((match) => ({
      level: match[1].length,
      text: match[2].trim(),
      id: slugifyHeading(match[2].trim()),
    }));
}

const markdownComponents = {
  h1: ({ children, ...props }) => (
    <Typography variant="h4" sx={{ fontWeight: 800, mt: 4, mb: 2, lineHeight: 1.18, fontSize: { xs: '1.4rem', sm: '1.65rem', md: '2rem' } }} {...props}>
      {children}
    </Typography>
  ),
  h2: ({ children, ...props }) => {
    const text = flattenChildrenToText(children);
    const id = slugifyHeading(text);

    return (
      <Box id={id} sx={{ scrollMarginTop: { xs: 88, md: 112 }, mt: { xs: 4, md: 5 }, mb: 2, minWidth: 0 }}>
        <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.2, fontSize: { xs: '1.25rem', sm: '1.45rem', md: '1.9rem' } }} {...props}>
          {children}
        </Typography>
      </Box>
    );
  },
  h3: ({ children, ...props }) => {
    const text = flattenChildrenToText(children);
    const id = slugifyHeading(text);

    return (
      <Box id={id} sx={{ scrollMarginTop: { xs: 88, md: 112 }, mt: { xs: 3.25, md: 4 }, mb: 1.5, minWidth: 0 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.28, fontSize: { xs: '1.05rem', sm: '1.14rem', md: '1.35rem' } }} {...props}>
          {children}
        </Typography>
      </Box>
    );
  },
  p: ({ ...props }) => <Typography paragraph sx={{ mb: 2.1, fontSize: { xs: '0.94rem', sm: '0.98rem', md: '1.05rem' }, lineHeight: { xs: 1.82, md: 1.9 }, color: 'text.primary' }} {...props} />,
  ul: ({ ...props }) => <Box component="ul" sx={{ pl: { xs: 2.4, md: 3 }, mb: 2.5, fontSize: { xs: '0.94rem', sm: '0.98rem', md: '1.03rem' }, lineHeight: { xs: 1.78, md: 1.85 } }} {...props} />,
  ol: ({ ...props }) => <Box component="ol" sx={{ pl: { xs: 2.4, md: 3 }, mb: 2.5, fontSize: { xs: '0.94rem', sm: '0.98rem', md: '1.03rem' }, lineHeight: { xs: 1.78, md: 1.85 } }} {...props} />,
  li: ({ children, ...props }) => (
    <Box component="li" sx={{ mb: 1.1 }} {...props}>
      <Typography component="span" sx={{ fontSize: { xs: '0.94rem', sm: '0.98rem', md: '1.03rem' }, lineHeight: { xs: 1.72, md: 1.8 } }}>
        {children}
      </Typography>
    </Box>
  ),
  strong: ({ ...props }) => <Box component="strong" sx={{ fontWeight: 800, color: '#0D1C4F' }} {...props} />,
  a: ({ ...props }) => <Link target="_blank" rel="noreferrer" sx={{ color: '#016f57', fontWeight: 700 }} {...props} />,
  hr: () => <Box component="hr" sx={{ border: 0, borderTop: '1px solid', borderColor: 'divider', my: 3 }} />,
  table: ({ ...props }) => (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch', my: { xs: 3, md: 4 }, border: '1px solid', borderColor: 'divider', borderRadius: { xs: 2, md: 3 } }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          minWidth: { xs: 420, sm: 500, md: 560 },
          borderCollapse: 'collapse',
          '& th, & td': {
            border: '1px solid',
            borderColor: 'divider',
            px: { xs: 1.25, sm: 1.5, md: 2 },
            py: { xs: 1, md: 1.25 },
            textAlign: 'left',
            verticalAlign: 'top',
            fontSize: { xs: '0.84rem', sm: '0.9rem', md: '0.96rem' },
          },
          '& th': {
            bgcolor: 'rgba(13, 28, 79, 0.04)',
            fontWeight: 700,
          },
        }}
        {...props}
      />
    </Box>
  ),
  code: ({ inline, children, ...props }) => {
    if (inline) {
      return (
        <Box
          component="code"
          sx={{
            px: 0.75,
            py: 0.2,
            borderRadius: 1,
            bgcolor: 'rgba(13, 28, 79, 0.06)',
            fontFamily: 'monospace',
            fontSize: '0.95em',
          }}
          {...props}
        >
          {children}
        </Box>
      );
    }

    return (
      <Box
        component="pre"
        sx={{
          p: { xs: 1.25, sm: 1.75, md: 2.25 },
          borderRadius: { xs: 2, md: 3 },
          overflowX: 'auto',
          maxWidth: '100%',
          bgcolor: 'rgba(13, 28, 79, 0.04)',
          border: '1px solid rgba(13, 28, 79, 0.08)',
          fontFamily: 'monospace',
          fontSize: { xs: '0.8rem', sm: '0.88rem', md: '0.95rem' },
          lineHeight: 1.6,
          mb: 2,
        }}
        {...props}
      >
        <code>{children}</code>
      </Box>
    );
  },
};

export default function MarkdownLegalPage({ markdownUrl, fallbackTitle }) {
  const [state, setState] = useState({
    loading: true,
    error: '',
    title: fallbackTitle,
    updatedAt: '',
    content: '',
    toc: [],
  });

  useEffect(() => {
    const controller = new AbortController();

    async function loadDocument() {
      try {
        const response = await fetch(markdownUrl, { signal: controller.signal });

        if (!response.ok) {
          throw new Error(`Unable to load document (${response.status})`);
        }

        const markdown = await response.text();
        const documentMetadata = extractDocumentMetadata(markdown, fallbackTitle);
        const toc = extractHeadings(documentMetadata.content);

        if (!controller.signal.aborted) {
          setState({
            loading: false,
            error: '',
            ...documentMetadata,
            toc,
          });
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setState((currentState) => ({
            ...currentState,
            loading: false,
            error: 'Unable to load this document right now.',
          }));
        }
      }
    }

    loadDocument();

    return () => controller.abort();
  }, [fallbackTitle, markdownUrl]);

  const summary =
    state.title === 'Privacy Policy'
      ? 'How PeerCampus collects, uses, stores, and protects personal data across the platform.'
      : 'The rules, responsibilities, and legal terms that govern use of PeerCampus and its services.';

  return (
    <LegalPage title={state.title} updatedAt={state.updatedAt} summary={summary} toc={state.toc}>
      {state.loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={24} />
          <Typography>Loading document...</Typography>
        </Box>
      ) : state.error ? (
        <Alert severity="error">{state.error}</Alert>
      ) : (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {state.content}
        </ReactMarkdown>
      )}
    </LegalPage>
  );
}